import { Injectable, signal } from '@angular/core';

const DEFAULT_DURATION = 4000;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<any[]>([]);
  readonly toasts = this._toasts.asReadonly();
  private idCounter = 0;

  show(config: any): number {
    const id = ++this.idCounter;
    const duration = config.duration ?? DEFAULT_DURATION;
    const toast: any = {
      id,
      message: config.message,
      type: config.type,
      duration,
      remaining: duration,
      timerStart: Date.now(),
      timeoutRef: null,
      paused: false,
    };
    this._toasts.update((list) => [...list, toast]);
    this.startTimer(toast);
    return id;
  }

  success(message: string, duration?: number): number {
    return this.show({ message, type: 'success', duration });
  }

  info(message: string, duration?: number): number {
    return this.show({ message, type: 'info', duration });
  }

  error(message: string, duration?: number): number {
    return this.show({ message, type: 'error', duration });
  }

  dismiss(id: number): void {
    const toast = this._toasts().find((t) => t.id === id);
    if (toast?.timeoutRef) {
      clearTimeout(toast.timeoutRef);
    }
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  pause(id: number): void {
    this._toasts.update((list) =>
      list.map((t) => {
        if (t.id !== id || t.paused) return t;
        if (t.timeoutRef) clearTimeout(t.timeoutRef);
        const elapsed = Date.now() - t.timerStart;
        return {
          ...t,
          paused: true,
          remaining: Math.max(t.remaining - elapsed, 0),
          timeoutRef: null,
        };
      }),
    );
  }

  resume(id: number): void {
    const toast = this._toasts().find((t) => t.id === id);
    if (!toast || !toast.paused) return;
    const updated: any = { ...toast, paused: false, timerStart: Date.now() };
    this._toasts.update((list) => list.map((t) => (t.id === id ? updated : t)));
    this.startTimer(updated);
  }

  clear(): void {
    this._toasts().forEach((t) => {
      if (t.timeoutRef) clearTimeout(t.timeoutRef);
    });
    this._toasts.set([]);
  }

  private startTimer(toast: any): void {
    if (toast.remaining <= 0) return;
    const timeoutRef = setTimeout(() => this.dismiss(toast.id), toast.remaining);
    this._toasts.update((list) =>
      list.map((t) => (t.id === toast.id ? { ...t, timeoutRef } : t)),
    );
  }
}
